import { useCallback, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import yup from 'utils/yup';
import { agentUserContext } from 'application/contexts/useAgentUser';
import { diContainerContext } from 'application/contexts/useDiContainer';
import { AgentUserRepository } from 'interfaceAdapter/repositories/AgentUser';
import { AgentUserQuery } from 'interfaceAdapter/queries/AgentUser';
import { useLoading } from 'application/contexts/useLoading';

const schema = yup
    .object({
        email: yup.string().required().email(),
        password: yup.string().required(),
    })
    .required();

type LoginForm = yup.InferType<typeof schema>;

export const useLoginForm = () => {
    const navigate = useNavigate();
    const diContainer = useContext(diContainerContext);
    const agentUserRepository = diContainer.resolve(AgentUserRepository);
    const agentUserQuery = diContainer.resolve(AgentUserQuery);
    const { setUser, urlAfterLogin } = useContext(agentUserContext);
    const { loading, setLoading } = useLoading();
    const {
        control,
        handleSubmit,
        formState: { errors },
    } = useForm<LoginForm>({
        resolver: yupResolver(schema),
        mode: 'onChange',
        defaultValues: {
            email: '',
            password: '',
        },
    });

    const redirectIfLoggedIn = useCallback(async () => {
        const { user, status } = await agentUserQuery.checkToken(true);
        if (status !== false) {
            setUser(user);
            navigate(urlAfterLogin);
        }
    }, [urlAfterLogin]);

    const login = handleSubmit(async ({ email, password }) => {
        setLoading(true);
        try {
            const user = await agentUserRepository.loginByEmail(email, password);
            // @ts-ignore
            setUser(user);
            navigate(urlAfterLogin);
        } finally {
            setLoading(false);
        }
    });

    return {
        control,
        errors,
        loading,
        redirectIfLoggedIn,
        login,
    };
};
